import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  apercuEnvoiDocument,
  apercuSignatureAttestation,
  envoyerSignatureAttestation,
  apercuEvaluation,
  envoyerEvaluation,
  envoyerDocumentSession,
  genererDocument,
  getDocuments,
  regenererPdfDocument,
  supprimerDocumentSession,
  telechargerPdfDocument,
  urlPdfDocument,
} from '../services/sessionService';
import ApercuEmail from './ApercuEmail';
import ActionsLigne from '../components/ActionsLigne';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import { formatDate } from './devisUtils';
import EnvoiConvocations from './EnvoiConvocations';
import CreerDepuisTextePanel from './CreerDepuisTextePanel';
import FeuillesPresence from './FeuillesPresence';
import ModeleEmailPanneau from './ModeleEmailPanneau';
import PanneauApercu from './PanneauApercu';
import SessionsPanel from './SessionsPanel';
import BoutonExport from './BoutonExport';
import FiltreDossier from './FiltreDossier';
import StatutsBarre from './StatutsBarre';
import { FACETTES_SEANCE, FACETTES_ENVOI_CONVOCATION } from './facettes';
import FiltresRepliables from '../components/FiltresRepliables';
import '../assets/styles/Devis.css';
import '../assets/styles/DemandeDevis.css';
import '../assets/styles/Listes.css';
import { FlashToast } from '../components/Toasts';
import { IconeOeil, IconeTelecharger } from '../components/IconesDocument';

const LIMIT = 15;

/**
 * SessionsPanel attend un contrat ; la ligne en porte les champs utiles
 * (cf. la requête de liste). On le reconstruit plutôt que de refaire un appel.
 */
function contratDeLaLigne(l) {
  return {
    id: l.contrat_id,
    numero: l.contrat_numero,
    societe_nom: l.societe_nom,
    intitule_action: l.intitule_action,
    lieu_formation: l.lieu_formation,
  };
}


/**
 * Convocations et feuilles de présence : même écran, seul le type change.
 * Les deux routes montent ce composant avec un `type` différent plutôt que
 * de dupliquer une page dont seuls les libellés diffèrent.
 */
const MODALITES = {
  presentiel: 'Présentiel',
  inter_presentiel: 'Inter présentiel',
  distanciel: 'Distanciel',
  mixte: 'Mixte',
};

/** Une session couvre plusieurs journées : on affiche la période, pas une date. */
function periode(jours) {
  if (!Array.isArray(jours) || jours.length === 0) return '—';
  if (jours.length === 1) return formatDate(jours[0]);
  return `${formatDate(jours[0])} → ${formatDate(jours[jours.length - 1])}`;
}

function DocumentsSession({ type, etape, titre, videTitre, videSousTitre, avecFormateur = false }) {
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [compteurs, setCompteurs] = useState({});
  const [page, setPage] = useState(1);

  // Dossier d'où l'on vient (boutons d'accès d'une demande, cf. Avancement) :
  // `cibleContact` peint ses lignes en vert et ne bouge plus, `filtreContact`
  // restreint la liste et se lève depuis le bandeau.
  const [params] = useSearchParams();
  const cibleContact = Number(params.get('contact')) || null;
  const cibleNom = params.get('client') || '';
  const [filtreContact, setFiltreContact] = useState(cibleContact);

  useEffect(() => {
    setFiltreContact(cibleContact);
  }, [cibleContact]);

  const [search, setSearch] = useState('');
  const [recherche, setRecherche] = useState('');
  const [seance, setSeance] = useState('');
  // Période sur la première journée de séance, comme les autres listes.
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  // Réglage de l'email propre à cette page (cf. ModeleEmailPanneau).
  const [panneauEmail, setPanneauEmail] = useState(false);
  // « Créer une convocation / une feuille » depuis un texte collé.
  const [creerOuvert, setCreerOuvert] = useState(false);
  // Planification du contrat dont le document est issu.
  const [contratOuvert, setContratOuvert] = useState(null);
  const [envoi, setEnvoi] = useState('');
  const [compteursEnvoi, setCompteursEnvoi] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfId, setPdfId] = useState(null);
  const [envoiOuvert, setEnvoiOuvert] = useState(null);
  // Feuilles de présence du contrat, ouvertes en cliquant la ligne.
  const [feuillesOuvertes, setFeuillesOuvertes] = useState(null);
  // Aperçu de la convocation, ouvert en cliquant la ligne.
  const [apercuOuvert, setApercuOuvert] = useState(null);
  const [flash, setFlash] = useState(null);
  const [ligneErreur, setLigneErreur] = useState(null);
  // Convocation du formateur en cours d'envoi (menu Formateurs).
  const [envoiFormateurId, setEnvoiFormateurId] = useState(null);
  // Génération des attestations depuis une ligne de feuille de présence.
  const [attestationId, setAttestationId] = useState(null);
  // Suppression d'un document (convocation, feuille…) : lui seul, la journée reste.
  // Libellés « ce document », par type, pour la confirmation et le message.
  const NOM_DOCUMENT = {
    convocation: { ce: 'cette convocation', le: 'La convocation' },
    feuille_presence: { ce: 'cette feuille de présence', le: 'La feuille de présence' },
    attestation: { ce: 'ces attestations', le: 'Les attestations' },
    convocation_formateur: { ce: 'cette convocation du formateur', le: 'La convocation du formateur' },
  };
  const nomDoc = (t) => NOM_DOCUMENT[t] || { ce: 'ce document', le: 'Le document' };
  const [suppression, setSuppression] = useState(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);

  async function handleSupprimer() {
    if (!suppression) return;
    const cible = suppression;
    setSuppressionEnCours(true);
    try {
      await supprimerDocumentSession(cible.id);
      setFlash(
        `${nomDoc(cible.type).le} de « ${cible.session_intitule} » supprimé${cible.type === 'attestation' ? 'es' : cible.type === 'convocation' || cible.type === 'feuille_presence' || cible.type === 'convocation_formateur' ? 'e' : ''}. La journée et ses autres documents restent.`
      );
      setSuppression(null);
      charger();
    } catch (err) {
      setLigneErreur({ id: cible.id, message: err.message || 'Suppression impossible.' });
      setSuppression(null);
    } finally {
      setSuppressionEnCours(false);
    }
  }
  // Feuille dont les liens d'émargement partent (pages Feuille de présence).

  // Changer d'onglet (convocation <-> feuille) doit repartir de la page 1 :
  // sinon on garde la pagination de la liste précédente, souvent hors bornes.
  useEffect(() => {
    setPage(1);
    setSearch('');
    setRecherche('');
    setSeance('');
    setEnvoi('');
    setDateDebut('');
    setDateFin('');
  }, [type, avecFormateur]);

  useEffect(() => {
    const t = setTimeout(() => {
      setRecherche(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const charger = useCallback(() => {
    setLoading(true);
    setError(null);
    getDocuments({
        type,
        page,
        limit: LIMIT,
        search: recherche,
        contactId: filtreContact,
        seance,
        envoi: type === 'feuille_presence' ? '' : envoi,
        dateDebut,
        dateFin,
        avecFormateur: avecFormateur ? 'true' : '',
      })
      .then((res) => {
        // Les pages du menu Formateurs comptent les cases du formateur ; les
        // autres, celles des participants. Chaque ligne compte les cases de
        // SA feuille uniquement : le total du contrat mélangeait les
        // journées et rendait le « 3/12 » illisible ligne par ligne.
        setDocuments(
          (res.data || []).map((d) => ({
            ...d,
            cases: avecFormateur ? d.nb_cases_formateur : d.nb_cases,
            signees: avecFormateur ? d.nb_emargements_formateur : d.nb_emargements,
            aSignerAujourdhui: avecFormateur
              ? d.formateur_a_signer_aujourdhui
              : d.a_signer_aujourdhui,
          }))
        );
        setTotal(res.pagination?.total ?? (res.data || []).length);
        setCompteurs(res.compteurs || {});
        setCompteursEnvoi(res.compteursEnvoi || {});
      })
      .catch((err) => setError(err.message || 'Impossible de charger les documents.'))
      .finally(() => setLoading(false));
  }, [type, page, recherche, filtreContact, seance, envoi, dateDebut, dateFin, avecFormateur]);

  useEffect(charger, [charger]);

  async function handlePdf(id) {
    setPdfId(id);
    setLigneErreur(null);
    try {
      await telechargerPdfDocument(id);
    } catch (err) {
      setLigneErreur({ id, message: err.message || 'Téléchargement impossible.' });
    } finally {
      setPdfId(null);
    }
  }

  // Une feuille de présence s'émarge pendant la séance : elle n'est pas
  // adressée au client, la dimension d'envoi n'a donc pas de sens pour elle.
  const suitLEnvoi = type !== 'feuille_presence';
  // Écrans du menu Formateurs : le formateur passe en tête de ligne, et ni la
  // planification ni les corrections ne s'y font — on y lit, on y envoie.
  const pourFormateur = type === 'convocation_formateur' || avecFormateur;
  // La feuille de présence se vit côté salle : c'est le formateur qui la fait
  // signer, la colonne le nomme donc lui plutôt que le client.
  const montreFormateur = pourFormateur || type === 'feuille_presence';

  // Rafraîchit l'iframe de l'aperçu après une régénération : même id de
  // document (upsert côté serveur), seul le fichier change.
  const [versionApercu, setVersionApercu] = useState(0);
  const [pdfRegenId, setPdfRegenId] = useState(null);

  /** « Régénérer le PDF » de la fiche ouverte : refait le fichier du document, sans le renvoyer. */
  const [feuilleGenId, setFeuilleGenId] = useState(null);

  /** Produit la feuille de présence de la journée d'une convocation, quand elle manque. */
  async function genererFeuille(d) {
    setFeuilleGenId(d.id);
    setLigneErreur(null);
    try {
      await genererDocument(d.contrat_session_id, 'feuille_presence');
      setFlash('Feuille de présence générée.');
      charger();
    } catch (err) {
      setLigneErreur({ id: d.id, message: err.message || 'Génération impossible.' });
    } finally {
      setFeuilleGenId(null);
    }
  }

  async function regenererPdf(d) {
    setPdfRegenId(d.id);
    setLigneErreur(null);
    try {
      const res = await regenererPdfDocument(d.id);
      setVersionApercu((v) => v + 1);
      setFlash(res.message || 'PDF refait.');
      charger();
    } catch (err) {
      setLigneErreur({ id: d.id, message: err.message || 'Régénération impossible.' });
    } finally {
      setPdfRegenId(null);
    }
  }

  /**
   * Refait le PDF des attestations depuis la fiche ouverte : la session a
   * pu changer (participants, dates, gabarit) et le fichier doit suivre.
   */
  async function regenererAttestation(d) {
    setAttestationId(d.id);
    setLigneErreur(null);
    try {
      await genererDocument(d.contrat_session_id, 'attestation');
      setVersionApercu((v) => v + 1);
      setFlash('Attestations régénérées : le PDF repart des données à jour de la session.');
      charger();
    } catch (err) {
      setLigneErreur({ id: d.id, message: err.message || 'Régénération impossible.' });
    } finally {
      setAttestationId(null);
    }
  }

  /** Sa convocation au formateur seul : le client n'est pas concerné. */
  /** Produit (ou reproduit) les attestations de la journée de cette feuille. */
  async function genererAttestations(d) {
    setAttestationId(d.id);
    setLigneErreur(null);
    try {
      await genererDocument(d.contrat_session_id, 'attestation');
      setFlash('Attestations générées : une page par participant, sur la page Attestation.');
      charger();
    } catch (err) {
      setLigneErreur({ id: d.id, message: err.message || 'Génération impossible.' });
    } finally {
      setAttestationId(null);
    }
  }

  // L'aperçu de l'email avant l'envoi : le document en cours de relecture.
  const [apercuEnvoiDoc, setApercuEnvoiDoc] = useState(null);

  /** Ouvre l'aperçu de l'email ; rien ne part avant le clic dans la modale. */
  function envoyerAuFormateur(d) {
    setLigneErreur(null);
    setApercuEnvoiDoc(d);
  }

  // Attestations : envoi du lien de signature à chaque participant. L'aperçu
  // montre le gabarit ; rien ne part avant le clic dans la modale.
  const [apercuSignatureDoc, setApercuSignatureDoc] = useState(null);
  const [signatureEnvoiId, setSignatureEnvoiId] = useState(null);

  // Évaluation de fin de formation (QCM) : le lien de chaque participant.
  const [apercuEvaluationDoc, setApercuEvaluationDoc] = useState(null);
  const [evaluationEnvoiId, setEvaluationEnvoiId] = useState(null);

  function envoyerEvaluationAuxParticipants(d) {
    setLigneErreur(null);
    setApercuEvaluationDoc(d);
  }

  /** L'envoi réel des liens d'évaluation, confirmé depuis l'aperçu. */
  async function envoyerEvaluationApresApercu(d, { sujet, texte }) {
    setEvaluationEnvoiId(d.id);
    try {
      const res = await envoyerEvaluation(d.contrat_session_id, { sujet, texte });
      setFlash(res.message || 'Évaluation envoyée.');
      charger();
    } finally {
      setEvaluationEnvoiId(null);
    }
  }

  function envoyerSignatureAuxParticipants(d) {
    setLigneErreur(null);
    setApercuSignatureDoc(d);
  }

  /** L'envoi réel des liens, confirmé depuis l'aperçu. */
  async function envoyerSignatureApresApercu(d, { sujet, texte }) {
    setSignatureEnvoiId(d.id);
    try {
      const res = await envoyerSignatureAttestation(d.id, { sujet, texte });
      setFlash(res.message || 'Liens de signature envoyés.');
      charger();
    } finally {
      setSignatureEnvoiId(null);
    }
  }

  /** L'envoi réel, confirmé depuis l'aperçu (texte éventuellement corrigé). */
  async function envoyerDocApresApercu(d, { sujet, texte }) {
    setEnvoiFormateurId(d.id);
    try {
      const res = await envoyerDocumentSession(d.id, { sujet, texte });
      setFlash(res.message || 'Document envoyé.');
      charger();
    } finally {
      setEnvoiFormateurId(null);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const rangeStart = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const rangeEnd = Math.min(page * LIMIT, total);
  const filtreActif = Boolean(recherche.trim() || dateDebut || dateFin);

  return (
    <div className="devis-page">
      <div className="devis-header">
        <div>
          <h1 className="devis-title">{titre}</h1>
        </div>
        {(suitLEnvoi || type === 'feuille_presence') && (
          <div className="devis-header-actions">
            {type === 'convocation' && (
              <BoutonExport
                chemin="/documents-session/export"
                filtres={{
                  type,
                  search: recherche,
                  contactId: filtreContact,
                  seance,
                  envoi,
                  dateDebut,
                  dateFin,
                }}
              />
            )}
            {type === 'convocation' && (
              <button
                type="button"
                className="devis-btn"
                onClick={() => setPanneauEmail(true)}
                title="Objet et contenu de l'email envoyé au client depuis cette page"
              >
                Paramétrage email
              </button>
            )}
            {suitLEnvoi && type !== 'attestation' && (
              <button
                type="button"
                className="devis-btn"
                onClick={() => setPanneauEmail('formateur')}
                title="Objet et contenu de l'email envoyé au formateur assigné, avec sa convocation"
              >
                Email formateur
              </button>
            )}
            {type === 'attestation' && (
              <button
                type="button"
                className="devis-btn"
                onClick={() => setPanneauEmail('evaluation')}
                title="Objet et contenu de l'email qui porte le lien du QCM d'évaluation, envoyé aux participants"
              >
                Email d’évaluation
              </button>
            )}
            {type === 'attestation' && (
              <button
                type="button"
                className="devis-btn"
                onClick={() => setPanneauEmail('signature')}
                title="Objet et contenu de l'email qui porte le lien de signature, envoyé aux participants"
              >
                Email de signature
              </button>
            )}
            {!pourFormateur && (type === 'convocation' || type === 'feuille_presence' || type === 'attestation') && (
              <button
                type="button"
                className="devis-btn devis-btn-primary"
                onClick={() => setCreerOuvert(true)}
                title={
                  type === 'convocation'
                    ? 'Créer une convocation depuis un texte collé (formation, dates, participants)'
                    : type === 'attestation'
                      ? 'Créer une attestation depuis un texte collé (formation, dates, participants)'
                      : 'Créer une feuille de présence depuis un texte collé (formation, dates)'
                }
              >
                {type === 'convocation'
                  ? 'Créer une convocation'
                  : type === 'attestation'
                    ? 'Créer une attestation'
                    : 'Créer une feuille de présence'}
              </button>
            )}
          </div>
        )}
      </div>

      <FiltreDossier
        nom={cibleNom}
        actif={Boolean(filtreContact)}
        total={total}
        onVoirTout={() => {
          setFiltreContact(null);
          setPage(1);
        }}
      />

      <FiltresRepliables libelle="Filtres">
        <div className="devis-toolbar">
          <input
            type="text"
            className="devis-search"
            placeholder="Rechercher une société, une session..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="devis-date-filter">
            <div className="devis-periode">
              <label className="devis-date-field">
                <span>Du</span>
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => {
                    setDateDebut(e.target.value);
                    setPage(1);
                  }}
                  max={dateFin || undefined}
                />
              </label>
              <label className="devis-date-field">
                <span>Au</span>
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => {
                    setDateFin(e.target.value);
                    setPage(1);
                  }}
                  min={dateDebut || undefined}
                />
              </label>
            </div>
            {filtreActif && (
              <button
                type="button"
                className="devis-date-clear"
                onClick={() => {
                  setSearch('');
                  setDateDebut('');
                  setDateFin('');
                  setPage(1);
                }}
              >
                Réinitialiser
              </button>
            )}
          </div>
          <span className="devis-total">{total} document{total > 1 ? 's' : ''}</span>
        </div>

        <StatutsBarre
          titre="Séance"
          options={FACETTES_SEANCE}
          compteurs={compteurs}
          valeur={seance}
          total={Object.values(compteurs).reduce((a, n) => a + (Number(n) || 0), 0)}
          libelleTous="Toutes"
          onSelect={(v) => {
            setSeance(v);
            setPage(1);
          }}
        />

        {suitLEnvoi && (
          <StatutsBarre
            titre="Envoi"
            options={FACETTES_ENVOI_CONVOCATION}
            compteurs={compteursEnvoi}
            valeur={envoi}
            total={Object.values(compteursEnvoi).reduce((a, n) => a + (Number(n) || 0), 0)}
            libelleTous="Toutes"
            onSelect={(v) => {
              setEnvoi(v);
              setPage(1);
            }}
          />
        )}

        <FlashToast texte={flash} onFermer={() => setFlash(null)} />
      </FiltresRepliables>


      <div className="devis-card">

        <div className="devis-table-wrap">
          {loading && <div className="devis-state">Chargement des documents...</div>}

          {!loading && error && <div className="devis-state devis-state-error">{error}</div>}

          {!loading && !error && documents.length === 0 && (
            <div className="devis-empty">
              <div className="devis-empty-title">{videTitre}</div>
              <div className="devis-empty-subtitle">{videSousTitre}</div>
            </div>
          )}

          {!loading && !error && documents.length > 0 && (
            <table className="devis-table devis-table-documents">
              <thead>
                <tr>
                  <th>SESSION</th>
                  <th>{pourFormateur ? 'FORMATEUR · CLIENT' : montreFormateur ? 'FORMATEUR' : 'CLIENT'}</th>
                  <th>SÉANCE</th>
                  <th>{suitLEnvoi ? 'ENVOI' : 'ÉMARGEMENT'}</th>
                  <th className="devis-th-actions">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => (
                  <tr
                    key={d.id}
                    className={
                      'devis-row' + (d.contact_id === cibleContact ? ' devis-row-cible' : '')
                    }
                    onClick={() =>
                      suitLEnvoi
                        ? setApercuOuvert(d)
                        : setFeuillesOuvertes({
                            contrat: contratDeLaLigne(d),
                            documentId: d.id,
                          })
                    }
                  >
                    <td>
                      <div className="devis-numero">{d.session_intitule}</div>
                      <div className="devis-contact-email">
                        {[d.groupe_libelle, `contrat ${d.contrat_numero}`].filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td data-label={pourFormateur ? 'FORMATEUR · CLIENT' : montreFormateur ? 'FORMATEUR' : 'CLIENT'}>
                      {montreFormateur ? (
                        <>
                          <div className="devis-contact-name">
                            {d.formateurs || 'Formateur/trice'}
                          </div>
                          <div className="devis-contact-email">
                            {[d.formateur_email, d.societe_nom].filter(Boolean).join(' · ')}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="devis-contact-name">{d.societe_nom}</div>
                          <div className="devis-contact-email">
                            {d.prenom} {d.nom} · {d.contact_email}
                          </div>
                        </>
                      )}
                    </td>
                    <td>
                      <div>{periode(d.jours)}</div>
                      <div className="devis-contact-email">
                        {[MODALITES[d.modalite] || d.modalite, d.lieu].filter(Boolean).join(' · ')}
                      </div>
                      {d.nb_participants != null && (
                        <span className="documents-effectif">
                          {d.nb_participants} participant{d.nb_participants > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td>
                      {/* La feuille de présence ne part à personne : son état
                          d'envoi n'aurait aucun sens, la colonne s'en tient
                          alors à la date de génération. */}
                      {suitLEnvoi ? (
                        <>
                          <span
                            className={`documents-envoi ${
                              d.date_envoi ? 'documents-envoi-parti' : 'documents-envoi-attente'
                            }`}
                          >
                            {d.date_envoi ? 'Envoyée' : 'À envoyer'}
                          </span>
                          {/* Un bouton grisé sans raison visible passe pour un
                              bug : la ligne dit ce qui bloque. */}
                          <div className="devis-contact-email">
                            {d.date_envoi
                              ? `le ${formatDate(d.date_envoi)}`
                              : d.documents_complets
                                ? `générée le ${formatDate(d.created_at)}`
                                : 'documents de la séance à compléter'}
                          </div>
                          {/* Attestations : où en sont les signatures des participants. */}
                          {type === 'attestation' && d.nb_participants > 0 && (
                            <div
                              className="devis-contact-email"
                              title="Participants qui ont signé leur attestation depuis leur lien"
                            >
                              {d.nb_signatures_attestation}/{d.nb_participants} signée
                              {d.nb_participants > 1 ? 's' : ''}
                              {d.signatures_envoyees_le
                                ? ` · liens envoyés le ${formatDate(d.signatures_envoyees_le)}`
                                : ' · liens non envoyés'}
                            </div>
                          )}
                          {/* Attestations : où en est l'évaluation de fin de formation. */}
                          {type === 'attestation' && (
                            <div
                              className="devis-contact-email"
                              title="Stagiaires qui ont répondu au QCM d'évaluation depuis leur lien"
                            >
                              {d.nb_evaluations_envoyees > 0
                                ? `QCM : ${d.nb_evaluations_faites}/${d.nb_evaluations_envoyees} réponse${
                                    d.nb_evaluations_faites > 1 ? 's' : ''
                                  }`
                                : 'QCM : non envoyé'}
                            </div>
                          )}
                          {/* Réponses aux boutons de l'email (migration 079). */}
                          {d.reponses && (
                            <div
                              className="devis-contact-email"
                              title="Réponses des destinataires aux boutons de la convocation"
                            >
                              {d.reponses.presents} présent{d.reponses.presents > 1 ? 's' : ''}
                              {' · '}
                              {d.reponses.absents} absent{d.reponses.absents > 1 ? 's' : ''}
                              {' · '}
                              {Math.max(0, d.reponses.attendues - d.reponses.presents - d.reponses.absents)}{' '}
                              sans réponse
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {d.cases > 0 ? (
                            <span
                              className={`documents-envoi ${
                                d.signees >= d.cases
                                  ? 'documents-envoi-parti'
                                  : 'documents-envoi-attente'
                              }`}
                            >
                              {d.signees}/{d.cases} signée
                              {d.cases > 1 ? 's' : ''}
                            </span>
                          ) : (
                            <div>aucune case à signer</div>
                          )}
                          <div className="devis-contact-email">
                            générée le {formatDate(d.created_at)}
                          </div>
                          {/* Consulter la feuille sans aller chercher la
                              colonne Actions : les signatures se vérifient
                              d'un clic, là où on lit leur compte. */}
                          <div
                            className="devis-actions-pile"
                            style={{ marginTop: 6 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier"
                              onClick={() =>
                                setFeuillesOuvertes({
                                  contrat: contratDeLaLigne(d),
                                  documentId: d.id,
                                })
                              }
                              title="Voir la feuille et ses signatures"
                            >
                              <IconeOeil />
                              PDF
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier"
                              onClick={() => handlePdf(d.id)}
                              disabled={pdfId === d.id}
                              title="Télécharger la feuille de présence en PDF"
                            >
                              {pdfId === d.id ? 'En cours' : (<><IconeTelecharger />PDF</>)}
                            </button>
                          </div>
                        </>
                      )}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {/* Planifier et envoyer engagent quelque chose : ils
                          prennent la largeur de la pile. Voir et PDF ne font
                          que consulter, ils se rangent en dessous. */}
                      <div className="devis-actions-pile">
                        {/* Le lien de signature part pour une case précise :
                            la feuille s'ouvre sur le choix de la journée et
                            de la demi-journée, puis envoie. */}
                        {!suitLEnvoi && (
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-primary devis-action-large"
                            onClick={() =>
                              setFeuillesOuvertes({
                                contrat: contratDeLaLigne(d),
                                documentId: d.id,
                                mode: 'envoi',
                              })
                            }
                            title={
                              avecFormateur
                                ? 'Envoie au formateur son lien pour signer une case de la feuille en ligne'
                                : 'Envoie à chaque participant son lien pour signer une case de la feuille en ligne'
                            }
                          >
                            Envoyer
                          </button>
                        )}
                        {!suitLEnvoi && d.aSignerAujourdhui && (
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-signer devis-action-large"
                            onClick={() =>
                              setFeuillesOuvertes({
                                contrat: contratDeLaLigne(d),
                                documentId: d.id,
                                mode: 'emargement',
                              })
                            }
                            title="La séance est aujourd’hui : des cases restent à émarger"
                          >
                            Signer
                          </button>
                        )}
                        {suitLEnvoi && !pourFormateur && (
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-dossier devis-action-large"
                            onClick={() => setContratOuvert(contratDeLaLigne(d))}
                            title="Sessions, participants et documents du contrat"
                          >
                            Planification
                          </button>
                        )}
                        {type === 'convocation_formateur' && (
                          <button
                            type="button"
                            className={`devis-btn devis-btn-sm devis-action-large ${
                              d.date_envoi ? 'devis-btn-envoye' : 'devis-btn-primary'
                            }`}
                            onClick={() => envoyerAuFormateur(d)}
                            disabled={envoiFormateurId === d.id}
                            title="Envoie sa convocation au formateur assigné, et à lui seul"
                          >
                            {envoiFormateurId === d.id
                              ? 'Envoi'
                              : d.date_envoi
                                ? 'Renvoyer'
                                : 'Envoyer'}
                          </button>
                        )}
                        {type === 'attestation' && (
                          <>
                            {/* La signature est l'affaire de chaque participant :
                                chacun reçoit SON lien, comme pour la feuille de
                                présence. */}
                            <button
                              type="button"
                              className={`devis-btn devis-btn-sm devis-action-large ${
                                d.signatures_envoyees_le ? 'devis-btn-envoye' : 'devis-btn-primary'
                              }`}
                              onClick={() => envoyerSignatureAuxParticipants(d)}
                              disabled={signatureEnvoiId === d.id}
                              title="Envoie à chaque participant, par email, son lien pour signer son attestation en ligne"
                            >
                              {signatureEnvoiId === d.id
                                ? 'Envoi'
                                : d.signatures_envoyees_le
                                  ? 'Renvoyer aux participants'
                                  : 'Envoyer aux participants'}
                            </button>
                            <button
                              type="button"
                              className={`devis-btn devis-btn-sm devis-action-large ${
                                d.nb_evaluations_envoyees > 0 ? 'devis-btn-envoye' : 'devis-btn-primary'
                              }`}
                              onClick={() => envoyerEvaluationAuxParticipants(d)}
                              disabled={evaluationEnvoiId === d.id}
                              title="Envoie à chaque participant son lien vers le QCM d'évaluation de la formation suivie ; il verra sa note à la fin"
                            >
                              {evaluationEnvoiId === d.id
                                ? 'Envoi'
                                : d.nb_evaluations_envoyees > 0
                                  ? 'Renvoyer l’évaluation'
                                  : 'Envoyer l’évaluation (QCM)'}
                            </button>
                            <button
                              type="button"
                              className={`devis-btn devis-btn-sm devis-action-large ${
                                d.date_envoi ? 'devis-btn-envoye' : ''
                              }`}
                              onClick={() => envoyerAuFormateur(d)}
                              disabled={envoiFormateurId === d.id}
                              title="Envoie les attestations (une page par participant) à l’interlocuteur du contrat"
                            >
                              {envoiFormateurId === d.id
                                ? 'Envoi'
                                : d.date_envoi
                                  ? 'Renvoyer au client'
                                  : 'Envoyer au client'}
                            </button>
                          </>
                        )}
                        {type === 'convocation' && (
                          <button
                            type="button"
                            className={`devis-btn devis-btn-sm devis-action-large ${
                              d.date_envoi ? 'devis-btn-envoye' : 'devis-btn-primary'
                            }`}
                            onClick={() =>
                              setEnvoiOuvert({
                                contrat: contratDeLaLigne(d),
                                documentId: d.id,
                              })
                            }
                            /* La feuille de présence de la même journée n’est
                               pas encore produite : le dossier n’est pas prêt
                               à partir. Le serveur refuse aussi cet envoi. */
                            disabled={!d.documents_complets}
                            title={
                              !d.documents_complets
                                ? 'Générez tous les documents de la session avant l’envoi (Planification)'
                                : d.date_envoi
                                  ? 'Renvoyer cette journée, ou en choisir d’autres du contrat'
                                  : 'Choisir les journées à convoquer, celle-ci cochée'
                            }
                          >
                            {d.date_envoi ? 'Renvoyer' : 'Envoyer'}
                          </button>
                        )}
                        {type === 'feuille_presence' && (
                          <button
                            type="button"
                            className="devis-btn devis-btn-sm devis-btn-primary devis-action-large"
                            onClick={() => genererAttestations(d)}
                            disabled={attestationId === d.id}
                            title="Génère (ou régénère) les attestations nominatives des participants de cette journée — elles se retrouvent sur la page Attestation"
                          >
                            {attestationId === d.id ? 'Génération…' : 'Générer attestations'}
                          </button>
                        )}
                        {/* La feuille de présence a déjà ses boutons de
                            consultation et de téléchargement dans la colonne
                            ÉMARGEMENT, sous le compte de signatures : ils ne
                            sont pas répétés ici. */}
                        {type === 'convocation' &&
                          (d.feuille_document_id ? (
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-action-large"
                              onClick={() =>
                                setFeuillesOuvertes({
                                  contrat: contratDeLaLigne(d),
                                  documentId: d.feuille_document_id,
                                })
                              }
                              title="Ouvrir la feuille de présence de cette convocation"
                            >
                              Feuille de présence
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-action-large"
                              onClick={() => genererFeuille(d)}
                              disabled={feuilleGenId === d.id}
                              title="La feuille de présence de cette journée n’est pas encore produite : la générer"
                            >
                              {feuilleGenId === d.id ? 'Génération…' : 'Générer la feuille'}
                            </button>
                          ))}
                        {type !== 'feuille_presence' && (
                          <>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier"
                              onClick={() => setApercuOuvert(d)}
                              title="Aperçu du document"
                            >
                              <IconeOeil />
                              PDF
                            </button>
                            <button
                              type="button"
                              className="devis-btn devis-btn-sm devis-btn-fichier"
                              onClick={() => handlePdf(d.id)}
                              disabled={pdfId === d.id}
                            >
                              {pdfId === d.id ? 'En cours' : (<><IconeTelecharger />PDF</>)}
                            </button>
                          </>
                        )}
                        {!pourFormateur && (
                          <ActionsLigne
                            onModifier={() => setContratOuvert(contratDeLaLigne(d))}
                            modifierTitre="Modifier la journée : dates, lieu, participants, formateur (le document est refait)"
                            onSupprimer={() => setSuppression(d)}
                            supprimerTitre={`Supprimer ${nomDoc(d.type).ce} — la journée et ses autres documents restent`}
                            supprimerDisabled={suppressionEnCours}
                          />
                        )}
                      </div>
                      {ligneErreur?.id === d.id && (
                        <div className="devis-row-erreur">{ligneErreur.message}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && !error && total > 0 && (
          <div className="devis-pagination">
            <span className="devis-pagination-info">
              {rangeStart}-{rangeEnd} sur {total}
            </span>
            <div className="devis-pagination-controls">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Précédent
              </button>
              <span className="devis-pagination-page">
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {panneauEmail && (
        <ModeleEmailPanneau
          type={
            panneauEmail === 'formateur'
              ? 'convocation_formateur'
              : panneauEmail === 'signature'
                ? 'attestation_signature'
                : panneauEmail === 'evaluation'
                  ? 'evaluation_qcm'
                  : 'convocation'
          }
          onClose={() => setPanneauEmail(false)}
        />
      )}

      {contratOuvert && (
        <SessionsPanel contrat={contratOuvert} onClose={() => setContratOuvert(null)} />
      )}

      {suppression && (
        <ConfirmationSuppression
          titre={`Supprimer ${nomDoc(suppression.type).ce} ?`}
          sousTitre={`${suppression.session_intitule} · ${suppression.contrat_numero} · ${suppression.societe_nom || ''}`}
          details={[
            `${nomDoc(suppression.type).le}, son PDF${suppression.type === 'feuille_presence' ? ' et les émargements déjà signés' : ''}.`,
          ]}
          note="La journée, ses autres documents (convocation, feuille, attestations), ses rappels, le contrat et le devis restent. Restaurable depuis la corbeille."
          enCours={suppressionEnCours}
          onAnnuler={() => setSuppression(null)}
          onConfirmer={handleSupprimer}
        />
      )}

      {creerOuvert && (
        <CreerDepuisTextePanel
          entite={type}
          onClose={() => setCreerOuvert(false)}
          onCree={(res) => {
            setFlash(res.message || 'Créé.');
            charger();
          }}
        />
      )}

      {apercuOuvert && (
        <PanneauApercu
          titre={apercuOuvert.session_intitule}
          sousTitre={`${apercuOuvert.contrat_numero} · ${apercuOuvert.societe_nom}`}
          url={`${urlPdfDocument(apercuOuvert.id, { apercu: true })}&v=${versionApercu}`}
          onClose={() => setApercuOuvert(null)}
          actions={
            <>
              {type === 'attestation' ? (
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => regenererAttestation(apercuOuvert)}
                  disabled={attestationId === apercuOuvert.id}
                  title="Refait le fichier des attestations à partir des données à jour de la session (participants, dates)"
                >
                  {attestationId === apercuOuvert.id ? 'Régénération…' : 'Régénérer le PDF'}
                </button>
              ) : (
                <button
                  type="button"
                  className="devis-btn devis-btn-sm"
                  onClick={() => regenererPdf(apercuOuvert)}
                  disabled={pdfRegenId === apercuOuvert.id}
                  title="Refait le PDF à partir des données à jour de la session"
                >
                  {pdfRegenId === apercuOuvert.id ? 'Régénération…' : 'Régénérer le PDF'}
                </button>
              )}
              {type === 'convocation_formateur' ? (
                <button
                  type="button"
                  className={`devis-btn devis-btn-sm ${
                    apercuOuvert.date_envoi ? 'devis-btn-envoye' : 'devis-btn-primary'
                  }`}
                  onClick={() => {
                    const doc = apercuOuvert;
                    setApercuOuvert(null);
                    envoyerAuFormateur(doc);
                  }}
                  disabled={envoiFormateurId === apercuOuvert.id}
                  title="Envoie sa convocation au formateur assigné, et à lui seul"
                >
                  {apercuOuvert.date_envoi ? 'Renvoyer' : 'Envoyer'}
                </button>
              ) : (
                <button
                  type="button"
                  className={`devis-btn devis-btn-sm ${
                    apercuOuvert.date_envoi ? 'devis-btn-envoye' : 'devis-btn-primary'
                  }`}
                  onClick={() => {
                    setEnvoiOuvert({
                      contrat: contratDeLaLigne(apercuOuvert),
                      documentId: apercuOuvert.id,
                    });
                    setApercuOuvert(null);
                  }}
                  disabled={!apercuOuvert.documents_complets}
                  title={
                    apercuOuvert.documents_complets
                      ? 'Choisir les journées à convoquer'
                      : 'Générez tous les documents de la session avant l’envoi'
                  }
                >
                  {apercuOuvert.date_envoi ? 'Renvoyer' : 'Envoyer'}
                </button>
              )}
              <button
                type="button"
                className="devis-btn devis-btn-sm devis-btn-fichier"
                onClick={() => handlePdf(apercuOuvert.id)}
                disabled={pdfId === apercuOuvert.id}
              >
                {pdfId === apercuOuvert.id ? 'En cours' : (<><IconeTelecharger />PDF</>)}
              </button>
            </>
          }
        />
      )}

      {feuillesOuvertes && (
        <FeuillesPresence
          contrat={feuillesOuvertes.contrat}
          documentId={feuillesOuvertes.documentId}
          modeInitial={feuillesOuvertes.mode}
          lectureSeule={pourFormateur}
          /* La page liste déjà toutes les feuilles : cliquer une ligne ouvre
             SA feuille, sans repasser par une seconde liste par contrat. */
          ouvrirDirectement
          onClose={() => setFeuillesOuvertes(null)}
        />
      )}

      {envoiOuvert && (
        <EnvoiConvocations
          contrat={envoiOuvert.contrat}
          documentId={envoiOuvert.documentId}
          onClose={() => setEnvoiOuvert(null)}
          onEnvoye={(message) => {
            setFlash(message);
            charger();
          }}
        />
      )}

      {apercuEvaluationDoc && (
        <ApercuEmail
          titre="Envoyer l’évaluation de fin de formation"
          sousTitre={apercuEvaluationDoc.session_intitule}
          charger={() => apercuEvaluation(apercuEvaluationDoc.contrat_session_id)}
          onEnvoyer={(edits) => envoyerEvaluationApresApercu(apercuEvaluationDoc, edits)}
          onClose={() => setApercuEvaluationDoc(null)}
        />
      )}

      {apercuSignatureDoc && (
        <ApercuEmail
          titre="Envoyer le lien de signature des attestations"
          sousTitre={apercuSignatureDoc.session_intitule}
          charger={() => apercuSignatureAttestation(apercuSignatureDoc.id)}
          onEnvoyer={(edits) => envoyerSignatureApresApercu(apercuSignatureDoc, edits)}
          onClose={() => setApercuSignatureDoc(null)}
        />
      )}

      {apercuEnvoiDoc && (
        <ApercuEmail
          titre={
            apercuEnvoiDoc.type === 'attestation'
              ? 'Envoyer les attestations'
              : apercuEnvoiDoc.type === 'feuille_presence'
                ? 'Envoyer la feuille de présence'
                : 'Envoyer la convocation au formateur'
          }
          sousTitre={apercuEnvoiDoc.session_intitule}
          charger={() => apercuEnvoiDocument(apercuEnvoiDoc.id)}
          onEnvoyer={(edits) => envoyerDocApresApercu(apercuEnvoiDoc, edits)}
          onClose={() => setApercuEnvoiDoc(null)}
        />
      )}
    </div>
  );
}

export default DocumentsSession;
